import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import Button from "components/Button/Button";
import { Input } from "components/inputs/Input/Input";
import { TextArea } from "components/inputs/TextArea";
import LoadingScreen from "components/Loading/LoadingScreen";
import { useAssetStore, useLayoutStore, useUserRolePermissionStore } from "contexts/RootStore.context";
import { Grid, Horizontal, Vertical } from "gls/lib";
import useDevice from "hooks/useDevice";
import { isEmpty } from "lodash";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { routePath } from "router/routePaths";
import { colours } from "styling/colours";
import { style } from "typestyle";
import { alertErrorMessage, alertSuccessMessage } from "utils/alerts";
import { MinimumLengthError, RequiredValidationError } from "utils/formstate";
import ConfirmCancelModal from "./ConfirmCancelModal";
import { useBlocker } from "hooks/useBlocker";
import { MAX_CHARACTERS_IN_ASSET_DESCRIPTION, MAX_CHARACTERS_IN_ASSET_NAME, MIN_CHARACTERS_IN_ASSET_NAME } from "config/constants";
import { AssetPermissions } from "views/config/roles/RoleConfigDto";
import ScrollingContainer from "components/ScrollContainer/ScrollingContainer";
import { SearchableSelect } from "components/inputs/Select/SearchableSelect";

export const AssetForm = observer(() => {

  const { isMobileDevice } = useDevice();
  const { assetForm, loadAsset, reset, dataLoading, error,
    isAnyChange, ischangesSaved, isFormEmpty, SaveAsset, saveLoading,
    getUnassignedBeacons, unassignedBeacons } = useAssetStore()
  const { getAssetTypes, getAssetOwners } = useUserRolePermissionStore()
  const params = useParams<{ assetId: string | undefined }>();
  const editMode = !isEmpty(params.assetId)
  const { setPageError } = useLayoutStore()
  const navigate = useNavigate()
  const [canelChangesModal, setCanelChangesModal] = useState(false)

  const { handleLeave, handleStay } = useBlocker(() => {
    if (!ischangesSaved && ((editMode && isAnyChange) || (!editMode && !isFormEmpty))) {
      setCanelChangesModal(true)
      return true
    }
    return false
  });

  const { asset_types, asset_owners } = useMemo(() => {
    return {
      asset_types: getAssetTypes(AssetPermissions.manage),
      asset_owners: getAssetOwners(AssetPermissions.manage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const abortController = new AbortController();

    Promise.all([
      getUnassignedBeacons(),
      params.assetId && loadAsset(params.assetId!)
    ]).finally(() => {
      if (abortController.signal.aborted) {
        reset();
      }
    })

    return () => {
      abortController.abort();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    setPageError(error)
  }, [error, setPageError])

  const saveAsset = () => {
    SaveAsset()
      .then(() => {
        if (params.assetId)
          alertSuccessMessage('Changes saved for the asset')
        else
          alertSuccessMessage(`${assetForm.$.asset_name.value} - 
      ${asset_types.find(r => r.value === assetForm.$.asset_type.value)?.label} has been added`)
        navigate(routePath.assetList)
      })
      .catch((error: ErrorResponseDto) => {
        if (error?.errorMsg)
          alertErrorMessage(error.errorMsg)
      })
  }

  if (dataLoading) {
    return <LoadingScreen />
  }
  return (
    <>
      <ScrollingContainer>
        <Vertical className={assetFormStyle(isMobileDevice)} >
          {editMode &&
            <Input
              label="Asset ID:"
              required
              className={fieldStyle}
              value={assetForm.$.asset_id.value}
              onChange={(value) => { assetForm.$.asset_id.onChange(value) }}
              disabled={editMode}
              id="asset_id"
              name="asset_id"
              maxLength={100}
              error={assetForm.$.asset_id.error !== RequiredValidationError ? assetForm.$.asset_id.error : ""}
            />
          }
          <Grid>
            <Input
              label="Asset Name:"
              required
              className={fieldStyle}
              value={assetForm.$.asset_name.value}
              onChange={(value) => { assetForm.$.asset_name.onChange(value) }}
              id="asset_name"
              name="asset_name"
              maxLength={MAX_CHARACTERS_IN_ASSET_NAME}
              showMaxLength
              minLength={MIN_CHARACTERS_IN_ASSET_NAME}
              error={assetForm.$.asset_name.error !== RequiredValidationError && assetForm.$.asset_name.error !== MinimumLengthError
                ? assetForm.$.asset_name.error : ""}
            />
            <SearchableSelect
              label="Asset Type:"
              required
              id="asset_types_select"
              options={asset_types.map(r => ({ key: r.label, value: r.value }))}
              value={assetForm.$.asset_type.value}
              onChange={assetForm.$.asset_type.onChange}
              className={fieldStyle}
            />
          </Grid>
          <Grid>
            <SearchableSelect
              label="Owner:"
              id="asset_owners_select"
              required
              options={asset_owners.map(r => ({ key: r.label, value: r.value }))}
              value={assetForm.$.owner.value}
              onChange={assetForm.$.owner.onChange}
              className={fieldStyle}
            />
            {!editMode &&
              <Input
                label="Asset ID:"
                required
                className={fieldStyle}
                value={assetForm.$.asset_id.value}
                onChange={(value) => { assetForm.$.asset_id.onChange(value) }}
                disabled={editMode}
                id="asset_id"
                name="asset_id"
                maxLength={100}
                error={assetForm.$.asset_id.error !== RequiredValidationError ? assetForm.$.asset_id.error : ""}
              />
            }
          </Grid>
          <TextArea
            id="description-input"
            label="Description:"
            value={assetForm.$.description.value}
            onChange={assetForm.$.description.onChange}
            className={fieldStyle}
            maxLength={MAX_CHARACTERS_IN_ASSET_DESCRIPTION}
            maxWidth="723px"
          />
          <div className={HrStyle}></div>
          <SearchableSelect
            options={unassignedBeacons}
            value={assetForm.$.assigned_beacon_id.value}
            onChange={assetForm.$.assigned_beacon_id.onChange}
            label="Assigned Beacon:"
            id="assigned-beacon-select"
            className={fieldStyle}
          />
        </Vertical>
        <Horizontal horizontalAlign="right" spacing={10}>
          <Button className="biggerButton"
            isLoading={saveLoading}
            outline
            onClick={() => navigate(routePath.assetList)}
            id="cancel-utton">
            Cancel
          </Button>
          <Button className="biggerButton"
            onClick={saveAsset}
            isLoading={saveLoading}
            id="save-button"
            disabled={!isAnyChange || assetForm.hasError}
          >
            {editMode ? 'Save' : 'Add'}
          </Button>
        </Horizontal>
      </ScrollingContainer>

      {canelChangesModal &&
        <ConfirmCancelModal
          editMode={editMode}
          onClose={() => {
            setCanelChangesModal(false)
            handleStay()
          }}
          onCancel={() => {
            setCanelChangesModal(false)
            handleLeave();
          }}
          onConfirm={() => {
            setCanelChangesModal(false)
            handleStay()
            if (!assetForm.hasError)
              saveAsset()
          }}
        />}
    </>
  );
});


const fieldStyle = style({
  minWidth: "350px",
  width: "350px",
});

const HrStyle = style({
  width: "100%",
  border: `1px solid ${colours.border}`,
})

const assetFormStyle = (isMobile: boolean) => style({
  backgroundColor: colours.white,
  padding: isMobile ? "20px" : "40px",
  marginBottom: "10px",
})